import {m as motion} from 'motion/react'
import styled, {css, keyframes} from 'styled-components'

import StyledButton from '@/components/button'
import {Text, text, NavTextLink} from '@/components/text'
import {Chevron} from '@/images/icons/chevron'
import {container} from '@/styles/grid'
import {focusStyles} from '@/styles/utils'
import {tokens, colors, headerSizes, zIndex, fonts} from '@/theme'
import mq from '@/utils/media-query'

interface WrapperProps {
    $menuOpen?: boolean
    $dropdownOpen?: boolean
}

export const mobileMenuBreakpoint = 'sm'
export const tabletMenuBreakpoint = 'md'
export const laptopMenuBreakpoint = 'lg'
const desktopMenuBreakpoint = 'xl'
const mobileMenuWidth = '480px'

const linksFocusStyles = css`
    &:focus {
        ${focusStyles()}
        border-radius: ${tokens.spacing4};
    }
`

const textLinkStyle = css`
    padding: 0;
    ${text({$variant: 'title-base'})}

    ${mq.min(laptopMenuBreakpoint)} {
        ${text({$variant: 'paragraph-base'})}
    }
`

const noUnderline = css`
    &,
    &:hover,
    &:focus,
    &:active {
        text-decoration: none;
    }
`

export const NavCta = styled(StyledButton)`
    font-size: ${tokens.fontLarge};
    transition: background ${tokens.fastIn};
`

const Logo = styled.div`
    * {
        display: block;
        height: 100%;
    }

    img {
        object-fit: contain;
    }

    a {
        ${linksFocusStyles}
    }
`

export const MobileLogo = styled(Logo)`
    display: none;
    width: 72px;
    padding: ${tokens.spacing10} 0; /* Increases height to match height when sticky CTA shows */
`

export const DesktopLogo = styled(Logo)`
    z-index: ${zIndex.headerLogo};
    display: block;
    width: 54px;
`

export const LinkArrow = styled.span`
    ${mq.max(laptopMenuBreakpoint)} {
        display: none;
    }
`

export const BetaTag = styled.span`
    display: none;

    ${mq.min(tabletMenuBreakpoint)} {
        display: inline-block;
        ${text({$variant: 'label-base-medium'})};
        padding: ${tokens.spacing4} ${tokens.spacing8};
        margin-left: ${tokens.spacing8};
        word-break: initial;
        vertical-align: middle;
        background: ${tokens.pink300};
        border-radius: ${tokens.arc4};
    }
`

export const Overlay = styled(motion.create('div'))<{
    $mobileMenuOpen: boolean
    $dropdownOpen: boolean
}>`
    position: fixed;
    z-index: ${zIndex.menuOverlay};
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${tokens.shade900lighten20};
    opacity: 0;

    ${mq.min(mobileMenuBreakpoint)} {
        opacity: 0.2;
    }

    ${mq.min(laptopMenuBreakpoint)} {
        display: ${(props) => props.$mobileMenuOpen && !props.$dropdownOpen && 'none'};
    }
`

export const MobileMenuBtn = styled.button`
    display: none;
    padding: 0;
    cursor: pointer;
    background: none;
    border: none;

    img {
        display: block;
        width: auto;
        height: 28px;
    }
`

export const NavMenu = styled.div`
    display: grid;
    width: 100%;
    gap: ${tokens.spacing20};

    ${mq.min(laptopMenuBreakpoint)} {
        gap: unset;
    }
`

export const MenuBtnWithDropdown = styled.button`
    ${textLinkStyle}
    background: none;
    border: none;
    width: fit-content;

    ${mq.min(tabletMenuBreakpoint)} {
        ${text({$variant: 'paragraph-base'})}
        padding: 0 ${tokens.spacing16};
        margin-bottom: 0;
        cursor: pointer;
        transition: color ${tokens.smoothInOut};
    }

    ${NavMenu} & {
        ${mq.min(tabletMenuBreakpoint)} {
            padding: 0 ${tokens.spacing10};
        }

        ${mq.min(laptopMenuBreakpoint)} {
            padding: 0 ${tokens.spacing12};
        }

        ${mq.min(desktopMenuBreakpoint)} {
            padding: 0 ${tokens.spacing16};
        }
    }

    /* Apply focused style only on keyboard interaction */
    &:focus-visible {
        ${linksFocusStyles}
    }
`

const NavGroup = styled.div`
    display: flex;
    align-items: center;
    ${text({$variant: 'paragraph-large'})}

    /* main nav but not child */
    & > a:not(${NavCta}) {
        ${mq.min(tabletMenuBreakpoint)} {
            padding-right: ${tokens.spacing10};
            padding-left: ${tokens.spacing10};
            margin-left: 0;
            text-align: center;
        }

        ${mq.min(laptopMenuBreakpoint)} {
            padding-right: ${tokens.spacing12};
            padding-left: ${tokens.spacing12};
        }

        ${mq.min(desktopMenuBreakpoint)} {
            padding-right: ${tokens.spacing16};
            padding-left: ${tokens.spacing16};
        }
    }
`

export const NavGroupLeft = styled(NavGroup)`
    display: grid;

    ${mq.min(laptopMenuBreakpoint)} {
        grid-template-columns: repeat(5, auto);
        padding-left: ${tokens.spacing12};
    }

    ${mq.min(desktopMenuBreakpoint)} {
        padding-left: ${tokens.spacing24};
    }
`

export const NavGroupRight = styled(NavGroup)`
    ${mq.max(laptopMenuBreakpoint)} {
        display: grid;
        gap: ${tokens.spacing20};
        grid-template-rows: auto 1fr;
        grid-template-columns: 1fr;
        height: 100%;
        padding-bottom: 160px;
    }

    ${mq.min(laptopMenuBreakpoint)} {
        align-items: center;
        margin-left: auto;
    }
`

type NavLinkProps = {
    $isHighlighted?: boolean
    $isDirectLink?: boolean
}

export const NavLink = styled.a<NavLinkProps>`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: ${tokens.spacing8};
    ${noUnderline}
    ${textLinkStyle}
    margin-bottom: ${(props) => (props.$isDirectLink ? tokens.spacing20 : 0)};

    ${mq.min(laptopMenuBreakpoint)} {
        gap: ${tokens.spacing4};
        ${text({$variant: 'paragraph-base'})}

        ${NavGroupRight} & {
            height: 100%;
        }

        ${NavGroup}:hover & {
            color: ${tokens.shade600};
            transition: color ${tokens.smoothInOut};

            &:hover {
                color: ${tokens.shade900};
            }
        }

        /* we need to win the specificity war with the hover styles below, 
        since the link should stay highlighted, even when the hover is gone */
        &&& {
            color: ${(props) => (props.$isHighlighted ? tokens.shade900 : undefined)};
        }

        &:focus-visible {
            ${linksFocusStyles}
        }
    }

    ${mq.min(desktopMenuBreakpoint)} {
        gap: ${tokens.spacing8};
    }
`

export const MenuBadge = styled.span`
    position: relative;
    top: -4px;
    width: 6px;
    height: 6px;
    background: ${tokens.pink700};
    border-radius: ${tokens.circle};
`

export const Arrow = styled(Chevron)<{$isExpanded: boolean}>`
    width: ${tokens.spacing16};
    height: ${tokens.spacing16};
    transition: transform ${tokens.smoothInOut};
    transform: ${(props) => props.$isExpanded && 'rotate(-180deg)'};

    ${mq.min(mobileMenuBreakpoint)} {
        width: ${tokens.spacing12};
        height: ${tokens.spacing12};
    }
`

export const DropdownWrapper = styled.div`
    left: 0;
    width: 100%;

    ${mq.min(laptopMenuBreakpoint)} {
        position: absolute;
        padding-top: ${tokens.spacing12};
    }
`

export const DropdownInner = styled.div`
    display: grid;
    ${container}
    padding-bottom: ${tokens.spacing12};
    background-color: ${tokens.shade000};

    ${mq.min(laptopMenuBreakpoint)} {
        justify-content: center;
        padding-top: ${tokens.spacing40};
        padding-bottom: ${tokens.spacing20};
        grid-template-columns: 1fr 3fr;
        gap: 144px;
    }

    /* WhyPleo dropdown */
    ${NavMenu}:nth-child(2) & {
        ${mq.max(laptopMenuBreakpoint)} {
            gap: ${tokens.spacing20};
        }
    }
`

export const Dropdown = styled.div`
    display: grid;
    padding-bottom: ${tokens.spacing12};
    background-color: ${tokens.shade000};
    border-bottom-right-radius: ${tokens.spacing48};

    ${mq.min(laptopMenuBreakpoint)} {
        justify-content: center;
        padding-bottom: ${tokens.spacing40};
    }
`

const nestedDropdownClosed = css`
    height: 0;
    overflow: hidden;
    opacity: 0;
`

const nestedDropdownOpen = css`
    overflow: visible;
    opacity: 1;
`

type NestedDropdownProps = {
    $isExpanded: boolean
    $direction?: 'row' | 'column'
    $hasOneColumn?: boolean
}

export const NestedDropdown = styled.div<NestedDropdownProps>(
    ({$isExpanded, $direction = 'row', $hasOneColumn}) => {
        return css`
            display: grid;
            transition: opacity ${tokens.fastInOut};

            ${mq.min(laptopMenuBreakpoint)} {
                grid-template-rows: repeat(3, auto);
                grid-auto-flow: ${$direction};
                ${nestedDropdownOpen}
            }

            ${mq.max(laptopMenuBreakpoint)} {
                padding-left: ${$hasOneColumn ? '0px' : tokens.spacing20};
                gap: ${tokens.spacing20};
                ${$isExpanded ? nestedDropdownOpen : nestedDropdownClosed}
            }
        `
    },
)

export const NestedDropdownCategoryTitle = styled.div`
    ${text({$color: 'muted', $variant: 'paragraph-large'})}

    ${mq.max(laptopMenuBreakpoint)} {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: ${tokens.spacing10};
        align-items: center;
        cursor: pointer;
    }
`

export const CtaWrapper = styled.div<{$menuOpen: boolean}>(({$menuOpen}) => {
    const menuOpenStyles = css`
        ${mq.max(laptopMenuBreakpoint)} {
            position: fixed;
            bottom: 0;
            left: 0;
            display: grid;
            width: 100%;
            padding: ${tokens.spacing20} ${tokens.spacing24} ${tokens.spacing40};
            background: ${tokens.shade000};
        }

        ${mq.between(mobileMenuBreakpoint, laptopMenuBreakpoint)} {
            right: 0;
            left: unset;
            width: ${mobileMenuWidth};
            padding: ${tokens.spacing20} ${tokens.spacing40} ${tokens.spacing40};
            border-bottom-left-radius: ${tokens.spacing48};
        }
    `

    return css`
        ${$menuOpen && menuOpenStyles}

        ${mq.min(laptopMenuBreakpoint)} {
            padding-left: ${tokens.spacing10};
        }

        ${mq.min(desktopMenuBreakpoint)} {
            padding-left: ${tokens.spacing16};
        }
    `
})

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`

export const StickyCtaWrapper = styled.div`
    display: block;
    padding: 0;
    animation: ${fadeIn} ${tokens.gradual};

    ${mq.min(mobileMenuBreakpoint)} {
        display: none;
    }
`

export const Nav = styled.nav`
    display: none;

    ${mq.min(mobileMenuBreakpoint)} {
        display: grid;
        justify-content: flex-end;
        padding-right: ${tokens.spacing40};
    }

    ${mq.min(laptopMenuBreakpoint)} {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 100%;
        padding-right: unset;
    }
`

const tabletNavStyles = css`
    ${NavGroupLeft} {
        display: none;
    }

    ${NavGroupRight} {
        display: inline-grid;
        padding-bottom: initial;
        grid-auto-flow: column;
        grid-template-rows: auto;
        gap: ${tokens.spacing40};
    }

    ${NavLink} {
        font-size: ${tokens.fontLarge};
        grid-template-columns: auto;
        gap: ${tokens.spacing4};
    }

    ${CtaWrapper} {
        display: grid;
    }

    ${NavCta} {
        ${textLinkStyle}
        background: none;
        border: none;
        border-radius: 0;

        ${mq.max(laptopMenuBreakpoint)} {
            font-family: ${fonts.families.secondary};
            font-size: ${tokens.fontLarge};
        }

        ${mq.between(mobileMenuBreakpoint, laptopMenuBreakpoint)} {
            text-decoration: underline;
        }

        &:hover {
            background: ${colors.background.pink};
        }
    }
`

const mobileMenuStyles = css`
    position: fixed;
    top: 0;
    bottom: 0;
    align-items: flex-start;

    ${MobileLogo} {
        display: none;
    }

    ${Nav} {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
        gap: ${tokens.spacing40};
        height: 100%;
        padding-top: 100px;

        ${mq.min(laptopMenuBreakpoint)} {
            padding-top: initial;
        }
    }

    ${NavGroup} {
        width: 100%;
        flex-direction: column;
        align-items: flex-start;

        @media (hover: hover) {
            a:hover {
                text-decoration: underline;
            }
        }
    }

    ${NavGroupRight} a:not(${NavCta}) {
        ${text({$variant: 'paragraph-large'})}
    }
`

export const HeaderInner = styled.div<WrapperProps>`
    ${container}
    display: grid;
    width: 100%;
    padding-right: 0;
    padding-left: 0;
    margin: 0 auto;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: ${(props) => (props.$menuOpen ? 'start' : 'center')};

    ${mq.min(tabletMenuBreakpoint)} {
        padding-right: 0;
        padding-left: 0;
    }

    ${mq.between(mobileMenuBreakpoint, laptopMenuBreakpoint)} {
        grid-template-columns: ${(props) => (props.$menuOpen ? 'auto' : 'auto 1fr')};
    }

    ${mq.min(laptopMenuBreakpoint)} {
        grid-template-columns: auto 1fr;
        align-items: center;
        padding-right: 0;
        padding-left: 0;
    }
`

// Adding fixed height from theme to accurately position
// elements around header / calculate scroll offsets.
// We bump the z-index when the mobile menu or any dropdown are open
// to ensure they are on top of other elements with the zIndex.header
// i.e the StickyNavSections
export const HeaderWrapper = styled.header<WrapperProps>(({$menuOpen, $dropdownOpen}) => {
    return css`
        position: sticky;
        top: 0;
        left: 0;
        z-index: ${$menuOpen || $dropdownOpen ? zIndex.header + 2 : zIndex.header};
        display: flex;
        width: 100%;
        padding: ${tokens.spacing16} ${tokens.spacing24};
        padding-bottom: ${$menuOpen && '0'};
        background: ${colors.background.default};
        align-items: center;

        ${mq.between(mobileMenuBreakpoint, laptopMenuBreakpoint)} {
            right: 0;
            left: unset;
            display: grid;
            width: ${$menuOpen ? mobileMenuWidth : '100%'};
            align-items: flex-end;
            grid-auto-flow: column;
            grid-template-columns: auto;
            border-bottom-left-radius: ${$menuOpen ? tokens.spacing48 : 0};

            ${!$menuOpen && tabletNavStyles}
        }

        ${mq.min(mobileMenuBreakpoint)} {
            padding-right: ${tokens.spacing40};
            padding-left: ${tokens.spacing40};
        }

        ${mq.max(headerSizes.breakpoint)} {
            overflow: auto;
            overscroll-behavior: contain;

            & > * + * {
                margin-left: 0;
            }

            ${MobileMenuBtn} {
                display: block;
                margin-top: ${tokens.spacing10};
            }

            ${DesktopLogo} {
                display: none;
            }

            ${MobileLogo} {
                display: block;
            }

            ${$menuOpen && mobileMenuStyles}
        }

        ${mq.min(headerSizes.breakpoint)} {
            height: ${headerSizes.heightLarge}px;
        }
    `
})

export const VisibleInMdAndLg = styled.span`
    ${mq.max(mobileMenuBreakpoint)} {
        display: none;
    }

    ${mq.min(desktopMenuBreakpoint)} {
        display: none;
    }
`

export const VisibleInSmAndXl = styled.span`
    ${mq.between(mobileMenuBreakpoint, desktopMenuBreakpoint)} {
        display: none;
    }
`

type ScrollTriggerProps = {
    $threshold: string
}

export const ScrollTrigger = styled.span<ScrollTriggerProps>`
    position: absolute;
    top: ${(props) => props.$threshold};
`

export const CTAWrapper = styled.div`
    justify-self: end;
`

type ColumnHeaderProps = {
    $isOnlyColumn?: boolean
}

export const ColumnHeader = styled.div<ColumnHeaderProps>`
    display: ${(props) => (props.$isOnlyColumn ? 'none' : 'grid')};
    grid-auto-flow: column;
    gap: ${tokens.spacing10};
    grid-template-columns: auto 1fr;
    align-items: center;

    ${mq.min(laptopMenuBreakpoint)} {
        display: grid;
        padding-left: ${tokens.spacing10};
    }

    img {
        display: none;

        ${mq.min(laptopMenuBreakpoint)} {
            display: block;
            max-width: 34px;
        }
    }
`

export const DropdownColumnsWrapper = styled.div`
    ${container};
    display: grid;
    padding-bottom: ${tokens.spacing12};

    ${mq.min(laptopMenuBreakpoint)} {
        padding-top: ${tokens.spacing40};
        padding-right: 0;
        padding-bottom: ${tokens.spacing20};
        padding-left: 0;
        gap: ${tokens.spacing60};
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    ${mq.min(desktopMenuBreakpoint)} {
        gap: 80px;
        padding-left: 85px;
    }
`

export const DropdownContainer = styled.div`
    display: grid;
    justify-content: end;
`

export const DropdownLinks = styled.div`
    display: grid;
    padding-left: ${tokens.spacing40};

    ${mq.min(tabletMenuBreakpoint)} {
        padding-left: 0;
    }
`

type BgColor = keyof Pick<(typeof colors)['background'], 'pink' | 'yellow' | 'grey'>

const bgColors: Record<BgColor, string> = {
    yellow: tokens.yellow300,
    grey: colors.background.muted,
    pink: colors.background.pink,
}

export const isAllowedBgColor = (color: string): color is BgColor =>
    Object.keys(bgColors).includes(color)

type DropdownAdProps = {
    $large: boolean
    $bgColor: BgColor
}

export const DropdownAd = styled.div<DropdownAdProps>`
    display: none;

    ${mq.min(laptopMenuBreakpoint)} {
        grid-column: ${(props) => props.$large && '2 / 4'};
        display: grid;
        grid-auto-flow: ${(props) => (props.$large ? 'column' : 'row')};
        grid-template-columns: ${(props) => props.$large && '1fr 1fr'};
        padding: ${tokens.spacing20};
        background-color: ${({$bgColor}) => bgColors[$bgColor]};
        border-radius: ${tokens.arc20};
        justify-items: center;
        align-items: center;
        gap: ${(props) => (props.$large ? tokens.spacing60 : tokens.spacing10)};
    }

    ${mq.min(desktopMenuBreakpoint)} {
        padding: ${tokens.spacing40};
    }
`

export const DropdownAdBodyWrapper = styled.div`
    display: grid;
    text-align: center;
    justify-items: center;
    gap: ${tokens.spacing32};
`

export const NavLinkWithHover = styled(NavTextLink)`
    ${text({$variant: 'paragraph-large'})}

    ${mq.min(laptopMenuBreakpoint)} {
        ${text({$variant: 'paragraph-base'})}
        padding: ${tokens.spacing10};
        border-radius: ${tokens.arc8};

        &:hover,
        &:active {
            text-decoration: none;
            background: ${colors.background.muted};
        }
    }
`

export const ColumnHeaderTitle = styled(Text)`
    ${text({$variant: 'paragraph-large'})}

    ${mq.min(laptopMenuBreakpoint)} {
        ${text({$variant: 'title-base'})}
    }
`

export const DropdownSingle = styled.div`
    padding-bottom: ${tokens.spacing12};
    background-color: ${tokens.shade000};
    border-bottom-right-radius: ${tokens.spacing48};

    ${mq.min(laptopMenuBreakpoint)} {
        padding-right: ${tokens.spacing40};
        padding-bottom: ${tokens.spacing40};
        padding-left: ${tokens.spacing28};
    }

    ${mq.min(desktopMenuBreakpoint)} {
        padding-left: ${tokens.spacing40};
    }
`

export const NestedDropdownColumn = styled.div<{$isExpanded: boolean}>`
    display: grid;
    grid-template-rows: auto 1fr;
    gap: ${tokens.spacing20};
    padding-bottom: ${(props) => (props.$isExpanded ? tokens.spacing20 : 0)};

    ${mq.min(laptopMenuBreakpoint)} {
        padding-top: ${tokens.spacing10};
        padding-bottom: unset;
        gap: ${tokens.spacing12};

        ${Arrow} {
            display: none;
        }
    }
`
